/**
 * Specialized Drawer Hooks
 * 
 * Domain-specific drawer hooks for common use cases.
 * Uses factory pattern to reduce duplication.
 */

import * as React from "react";
import { useGlobalDrawerStack } from "../context.js";
import type { DrawerConfig, GlobalDrawerId } from "../types.js";

/**
 * Factory function to create specialized drawer hooks
 * 
 * @param drawerId - The drawer ID
 * @param defaultConfig - Default configuration for this drawer
 * @returns Hook function
 */
export function createSpecializedDrawerHook(
  drawerId: GlobalDrawerId,
  defaultConfig?: Partial<DrawerConfig>
) {
  return function useSpecializedDrawer() {
    const { openDrawer, closeDrawer, isDrawerOpen } = useGlobalDrawerStack();

    const open = React.useCallback(
      (config?: Partial<DrawerConfig>) => {
        openDrawer(drawerId, {
          ...defaultConfig,
          ...config,
        });
      },
      [openDrawer]
    );

    const close = React.useCallback(() => {
      closeDrawer(drawerId);
    }, [closeDrawer]);

    const isOpen = isDrawerOpen(drawerId);

    return React.useMemo(
      () => ({
        open,
        close,
        isOpen,
      }),
      [open, close, isOpen]
    );
  };
}

/**
 * Hook for managing message detail drawer
 * Provides convenient methods for opening/closing the message detail drawer
 */
export const useMessageDetailDrawer = createSpecializedDrawerHook(
  'mobile-message-detail',
  {
    side: 'bottom',
    height: '90vh',
    enableSwipeGesture: true,
    showCloseButton: true,
  }
);
